import { Permissions } from "./../../../../core/constants/permissions";
import { AuthService } from "./../../../../core/services/auth.service";
import {
  FullPatientPaymentInfo,
  PatientBasicInfo,
} from "./../../../../core/models/patient.model";
import { FormControl, FormGroup, ValidatorFn, Validators, } from "@angular/forms";
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, } from "@angular/core";
import { BankRequirements, } from "../../../../core/models/payment.models";
import {
  BankTransferMethod,
  PatientPaymentInfoUpdateRequest,
  PaymentMethod,
} from "../../../../core/models/patient.model";
import { SortCodeValidator } from "../../../../core/validators/sort-code.validator";
import { PatientService } from "../../../../core/services/patient.service";
import { AlertService } from "../../../../shared/alert/alert.service";
import { IbanRegex } from "../../../../core/constants/ibanRegex";
import { BankAccount } from "../../../../core/models/bank-account.model";
import { BeneficiariesListModalComponent } from "./beneficiaries-list-modal/beneficiaries-list-modal.component";
import { PaymentInfoGeneralSettingsComponent } from "./payment-info-general-settings/payment-info-general-settings.component";
import { PaymentInfoCaxtonBankComponent } from "./payment-info-caxton-bank/payment-info-caxton-bank.component";

@Component({
  selector: "app-patient-payment-info",
  templateUrl: "./patient-payment-info.component.html",
  styleUrls: ["./patient-payment-info.component.scss"]
})
export class PatientPaymentInfoComponent implements OnInit {
  @ViewChild("beneficiariesListModal") beneficiariesListModal: BeneficiariesListModalComponent;

  @ViewChild('paymentInfoGeneralSettings') paymentInfoGeneralSettings: PaymentInfoGeneralSettingsComponent;
  @ViewChild('paymentInfoCaxtonBank') paymentInfoCaxtonBank: PaymentInfoCaxtonBankComponent;

  @Input() trialId: string;
  @Input() patientTrialId: string;
  @Input() patientBasicInfo: PatientBasicInfo;

  paymentInfoForm = new FormGroup({
    defaultPaymentMethod: new FormControl<PaymentMethod>(null),
    bankTransferMethod: new FormControl<BankTransferMethod>(null),
    caxtonWalletName: new FormControl(""),
    caxtonWalletId: new FormControl({ value: "", disabled: true }),
    beneficiaryId: new FormControl({ value: "", disabled: true }),
    country: new FormControl({ value: "", disabled: true }),
    currency: new FormControl({ value: "", disabled: true }),
    iban: new FormControl({ value: "", disabled: true }),
    sortCode: new FormControl({ value: "", disabled: true }),
    accountNumber: new FormControl({ value: "", disabled: true }),
    swift: new FormControl({ value: "", disabled: true }),
    title: new FormControl(""),
    firstName: new FormControl({ value: "", disabled: true }),
    lastName: new FormControl({ value: "", disabled: true }),
    initialBankCoverFee: new FormControl(null),
    bankCoverFeeCurrency: new FormControl(""),
    accountOwnerAddress: new FormControl({ value: "", disabled: true }),
  });

  bankRequirements: BankRequirements = {
    usesIban: false,
    usesSortCode: false,
    usesSwift: false
  };

  currentBeneficiaryId: string;
  previousBeneficiaryIds: string[] = [];
  updateBeneficiary: boolean = false;
  isFormProcessing: boolean = false;
  formInitialized: boolean = false;
  trialCaxtonEnabled: boolean = false;
  invalidForm: boolean = false;
  currency: string;
  Permissions = Permissions;
  bankAccount = new BankAccount({ bankAccountEnabled: false });

  selectedTab = "general-settings";

  patientPaymentInfo: FullPatientPaymentInfo;
  BankTransferMethod = BankTransferMethod;
  PaymentMethod = PaymentMethod;

  constructor(
    private patientService: PatientService,
    private alertService: AlertService,
    private changeDetector: ChangeDetectorRef,
    public authService: AuthService,
  ) { }

  ngOnInit(): void {
    if (this.authService.hasPermission(Permissions.PatientPaymentInfoReadBankDetails)) {
      this.getFullPatientPaymentInfo();
    }
    else {
      this.getPatientPaymentInfo();
    }
  }

  getPatientPaymentInfo() {
    this.patientService.getPatientPaymentInfo(this.patientTrialId).subscribe({
      next: (response) => {
        this.patientPaymentInfo = response as FullPatientPaymentInfo;

        this.paymentInfoForm.patchValue({
          defaultPaymentMethod: response.defaultPaymentMethod,
          bankTransferMethod: response.bankTransferMethod,
          caxtonWalletName: response.caxtonWalletName,
          caxtonWalletId: response.caxtonWalletId
        });

        this.paymentInfoGeneralSettings.bankTransferMethodSelect.setValue(response.bankTransferMethod.toString());
        this.paymentInfoGeneralSettings.defaultPaymentMethodSelect.setValue(response.defaultPaymentMethod.toString());

        this.paymentInfoGeneralSettings.bankTransferMethodSelect.disabled = true;
        this.paymentInfoGeneralSettings.defaultPaymentMethodSelect.disabled = true;

        this.formInitialized = true;
      },
      error: () => {
        this.alertService.showWarningAlert("Failed to get patient payment info. Please try again or contact an administrator");
      },
    });
  }

  public getFullPatientPaymentInfo() {
    this.patientService.getFullPatientPaymentInfo(this.patientTrialId).subscribe({
      next: (response) => {
        this.patientPaymentInfo = response;

        this.previousBeneficiaryIds = response.previousBeneficiaryIds;
        this.trialCaxtonEnabled = response.trialCaxtonEnabled;

        if (response.trialNmibleEnabled && response.trialVirtualCardInAppEnabled)
        {
          this.paymentInfoGeneralSettings.addNmibleCardOption();
        }

        if (response.trialNmibleEnabled)
        {
          this.paymentInfoGeneralSettings.addNmibleBankOption();
        }

        this.paymentInfoForm.patchValue({
          defaultPaymentMethod: response.defaultPaymentMethod,
          bankTransferMethod: response.bankTransferMethod,
          beneficiaryId: response.caxtonBeneficiaryId,
          caxtonWalletName: response.caxtonWalletName,
          caxtonWalletId: response.caxtonWalletId,
          firstName: response.firstName,
          lastName: response.lastName,
          title: response.title,
          accountOwnerAddress: response.accountOwnerAddress,
          initialBankCoverFee: response.initialBankCoverFee,
          bankCoverFeeCurrency: response.bankCoverFeeCurrency,
        });

        if (!this.authService.hasPermission(Permissions.PatientPaymentInfoWrite)) {
          this.paymentInfoForm.get('initialBankCoverFee').disable();
          this.paymentInfoForm.get('bankTransferMethod').disable();
          this.paymentInfoForm.get('caxtonWalletName').disable();
          this.paymentInfoForm.get('currency').disable();
          this.paymentInfoForm.get('bankCoverFeeCurrency').disable();
        }

        this.paymentInfoGeneralSettings.bankTransferMethodSelect?.setValue(response.bankTransferMethod.toString());
        this.paymentInfoGeneralSettings.defaultPaymentMethodSelect?.setValue(response.defaultPaymentMethod.toString());
        this.paymentInfoGeneralSettings.bankCoverFeeCurrencySelect?.setValue(response.bankCoverFeeCurrency);

        if ((!response.trialNmibleEnabled || !response.trialVirtualCardInAppEnabled) && response.defaultPaymentMethod === PaymentMethod.NmibleCard)
        {
          this.paymentInfoGeneralSettings.setNmibleCardDisplayOnly();
        }

        if (!response.trialNmibleEnabled && response.defaultPaymentMethod === PaymentMethod.NmibleBank)
        {
          this.paymentInfoGeneralSettings.setNmibleBankDisplayOnly();
        }

        this.paymentInfoGeneralSettings.bankFeeCoverData = response.bankCoverFees;

        if (response.beneficiaryDetails) {
          this.paymentInfoForm.patchValue({
            iban: response.beneficiaryDetails.iban,
            sortCode: response.beneficiaryDetails.sortCode,
            swift: response.beneficiaryDetails.swift,
            accountNumber: response.beneficiaryDetails.accountNumber,
            currency: response.beneficiaryDetails.currency,
            country: response.beneficiaryDetails.country
          });

          this.bankRequirements = response.bankRequirements;

          this.changeDetector.detectChanges();
        }

        if (response.bankTransferMethod === BankTransferMethod.Automated && response.defaultPaymentMethod == PaymentMethod.CaxtonCard) {
          this.paymentInfoForm.get("caxtonWalletName").setValidators([Validators.required]);
        }

        this.formInitialized = true;
        this.paymentInfoForm.markAsPristine();
        this.subscribePaymentMethodValueChanges();
        this.subscribeBankTransferMethodValueChanges();
      },
      error: () => {
        this.alertService.showWarningAlert("Failed to get patient payment info. Please try again or contact an administrator");
      },
    });
  }

  setSelectedTab(tabName: string) {
    this.selectedTab = tabName;
  }

  private subscribePaymentMethodValueChanges() {
    this.paymentInfoForm.get("defaultPaymentMethod").valueChanges.subscribe((value) => {
      this.handlePaymentChange(value as PaymentMethod, this.paymentInfoForm.get("bankTransferMethod").value as BankTransferMethod);
    });
  }

  private subscribeBankTransferMethodValueChanges() {
    this.paymentInfoForm.get("bankTransferMethod").valueChanges.subscribe((value) => {
      this.handlePaymentChange(this.paymentInfoForm.get("defaultPaymentMethod").value as PaymentMethod, value as BankTransferMethod);
    });
  }

  private handlePaymentChange(paymentMethod: PaymentMethod, bankTransferMethod: BankTransferMethod) {
    if (paymentMethod === PaymentMethod.BankTransfer && bankTransferMethod === BankTransferMethod.Automated) {
      this.invalidForm = this.paymentInfoForm.get("beneficiaryId").value === null;

      this.paymentInfoGeneralSettings.bankCoverFeeCurrencySelect.setValue(this.paymentInfoForm.get("currency").value);

      this.paymentInfoForm.get("title").setValidators(Validators.required);
      this.paymentInfoForm.get("firstName").setValidators([Validators.required, Validators.pattern('^[A-Za-z ]+$')]);
      this.paymentInfoForm.get("lastName").setValidators([Validators.required, Validators.pattern('^[A-Za-z ]+$')]);
    } else {
      this.paymentInfoForm.get("title").clearValidators();
      this.paymentInfoForm.get("firstName").clearValidators();
      this.paymentInfoForm.get("lastName").clearValidators();

      this.paymentInfoForm.get("title").setErrors(null);
      this.paymentInfoForm.get("firstName").setErrors(null);
      this.paymentInfoForm.get("lastName").setErrors(null);
      this.invalidForm = false;
    }

    if (paymentMethod === PaymentMethod.CaxtonCard && bankTransferMethod === BankTransferMethod.Automated) {
      this.paymentInfoForm.get("caxtonWalletName").setValidators(Validators.required);
    } else {
      this.paymentInfoForm.get("caxtonWalletName").clearValidators();
      this.paymentInfoForm.get("caxtonWalletName").setErrors(null);
    }
  }

  setValidation(controlName: string, condition: boolean, validators: ValidatorFn[]) {
    if (condition) {
      this.paymentInfoForm.get(controlName).setValidators(validators);
    } else {
      this.paymentInfoForm.get(controlName).clearValidators();
      this.paymentInfoForm.get(controlName).setErrors(null);
      this.paymentInfoForm.get(controlName).setValue("");
    }

    this.paymentInfoForm.get(controlName).updateValueAndValidity();
  }

  updatePatient() {
    this.isFormProcessing = true;

    let request: PatientPaymentInfoUpdateRequest = this.paymentInfoForm.getRawValue() as unknown as PatientPaymentInfoUpdateRequest;
    request.bankCoverFees = this.paymentInfoGeneralSettings.bankFeeCoverData;
    request.patientTrialId = this.patientTrialId;

    this.setCaxtonData(request);

    this.patientService.updatePatientPaymentInfo(request).subscribe({
      next: (result) => {
        if (result.nameCurrencyBeneficiaryExists) {
          this.isFormProcessing = false;
          this.showBeneficiariesListModal(result);
          return;
        }

        this.isFormProcessing = false;
        this.cancel();
        this.paymentInfoForm.get("beneficiaryId").setValue(result.caxtonBeneficiaryId);

        if (this.currentBeneficiaryId) {
          this.previousBeneficiaryIds.push(this.currentBeneficiaryId);
        }

        this.currentBeneficiaryId = result.caxtonBeneficiaryId;
        this.alertService.showSuccessAlert("Patient updated successfully");
      },
      error: (error) => {
        this.isFormProcessing = false;
        this.alertService.showWarningAlert(`${error.error.title}`);
      },
    });
  }

  showBeneficiariesListModal(updateResult: FullPatientPaymentInfo) {
    this.beneficiariesListModal.beneficiaries = updateResult.allSubsidiaryBeneficiaries;
    this.beneficiariesListModal.filteredBeneficiaries = updateResult.allSubsidiaryBeneficiaries;
    this.beneficiariesListModal.selectedBeneficiary = updateResult.existingMatchingBeneficiary;
    this.beneficiariesListModal.patientTrialId = this.patientTrialId;

    this.beneficiariesListModal.modal.show();
  }

  validateName(): boolean {
    let allowedCharactersRegex: RegExp = /^[a-zA-Z ]+$/;
    let validFirstName = allowedCharactersRegex.test(
      this.paymentInfoForm.get("firstName").value
    );
    let validLastName = allowedCharactersRegex.test(
      this.paymentInfoForm.get("lastName").value
    );

    if (!validFirstName || !validLastName) {
      this.alertService.showWarningAlert("First Name and Last Name fields can only contain alphabetical characters");
    }

    return validFirstName && validLastName;
  }

  setCaxtonData(request: PatientPaymentInfoUpdateRequest) {
    let iban = this.paymentInfoForm.get("iban").value;
    let sortCode = this.paymentInfoForm.get("sortCode").value;
    let swift = this.paymentInfoForm.get("swift").value;
    let accountNumber = this.paymentInfoForm.get("accountNumber").value;

    if (iban?.length ||
      sortCode?.length ||
      swift?.length ||
      accountNumber?.length) {
      request.beneficiaryDetails = {
        country: this.paymentInfoForm.get("country").value,
        currency: this.paymentInfoForm.get("currency").value,
        iban: iban,
        sortCode: sortCode,
        accountNumber: accountNumber,
        swift: swift,
      };

      if (request.defaultPaymentMethod.toString() === PaymentMethod.BankTransfer.toString() &&
        request.bankTransferMethod.toString() === BankTransferMethod.Automated.toString()) {
        request.bankCoverFeeCurrency = request.beneficiaryDetails.currency;
      }
    }
  }

  cancel() {
    this.updateBeneficiary = false;
    this.paymentInfoForm.get("beneficiaryId").setValue(this.currentBeneficiaryId);

    this.paymentInfoForm.get("title").disable();
    this.paymentInfoForm.get("country").disable();
    this.paymentInfoForm.get("currency").disable();
    this.paymentInfoForm.get("firstName").disable();
    this.paymentInfoForm.get("lastName").disable();
    this.paymentInfoForm.get("iban").disable();
    this.paymentInfoForm.get("sortCode").disable();
    this.paymentInfoForm.get("accountNumber").disable();
    this.paymentInfoForm.get("swift").disable();
    this.paymentInfoForm.get("accountOwnerAddress").disable();

    if (this.paymentInfoCaxtonBank) {
      this.paymentInfoCaxtonBank.titleSelect.disabled = true;
    }

    if (this.paymentInfoForm.get("defaultPaymentMethod").value === PaymentMethod.BankTransfer &&
      this.paymentInfoForm.get("bankTransferMethod").value === BankTransferMethod.Automated) {
      this.invalidForm = this.paymentInfoForm.get("beneficiaryId").value === null;
    }
  }

  editBeneficiary() {
    this.currentBeneficiaryId = this.paymentInfoForm.get("beneficiaryId").value;
    this.paymentInfoForm.get("beneficiaryId").setValue("");

    this.paymentInfoForm.get("country").enable();
    this.paymentInfoForm.get("currency").enable();
    this.paymentInfoForm.get("firstName").enable();
    this.paymentInfoForm.get("lastName").enable();
    this.paymentInfoForm.get("iban").enable();
    this.paymentInfoForm.get("sortCode").enable();
    this.paymentInfoForm.get("accountNumber").enable();
    this.paymentInfoForm.get("swift").enable();
    this.paymentInfoForm.get("accountOwnerAddress").enable();

    this.paymentInfoForm.get("title").setValidators(Validators.required);
    this.paymentInfoForm.get("firstName").setValidators([Validators.required, Validators.pattern('^[A-Za-z ]+$')]);
    this.paymentInfoForm.get("lastName").setValidators([Validators.required, Validators.pattern('^[A-Za-z ]+$')]);

    this.setValidation("sortCode", this.bankRequirements.usesSortCode, [
      Validators.required,
      SortCodeValidator()
    ]);
    this.setValidation("swift", this.bankRequirements.usesSwift, [
      Validators.required,
      Validators.pattern(
        /^([a-zA-Z]){4}([a-zA-Z]){2}([0-9a-zA-Z]){2}([0-9a-zA-Z]{3})?$/
      )
    ]);
    this.setValidation(
      "accountNumber",
      this.bankRequirements.usesSortCode || this.bankRequirements.usesSwift,
      [Validators.required]
    );
    this.setValidation("iban", this.bankRequirements.usesIban, [
      Validators.required,
      Validators.pattern(
        IbanRegex.all()[this.paymentInfoForm.get('country').value]?.regex
      )
    ]);

    this.paymentInfoForm.get("title").updateValueAndValidity();
    this.updateBeneficiary = true;
    this.invalidForm = false;
  }

  updateBeneficiaryId(beneficiaryId: string) {
    this.currentBeneficiaryId = beneficiaryId;
    this.cancel();
  }
}
